import axios, { AxiosError } from "axios";

export type AppErrorKind =
  | "offline"
  | "network"
  | "timeout"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "rate_limited"
  | "server"
  | "validation"
  | "unknown";

export interface AppError {
  kind: AppErrorKind;
  status?: number;
  message: string;
  raw: unknown;
  retryAfter?: number;
  // Backward-compatibility: expose original axios fields so call sites
  // that pre-date the classifier (e.g. err.response.data.message[0]...) keep
  // working without rewriting every catch block.
  response?: any;
  config?: any;
  isAxiosError?: boolean;
}

const isAxiosError = (err: unknown): err is AxiosError =>
  axios.isAxiosError(err);

const isAppError = (err: unknown): err is AppError =>
  typeof err === "object" &&
  err !== null &&
  "kind" in err &&
  "message" in err &&
  "raw" in err;

const withAxiosFields = (base: AppError, err: unknown): AppError => {
  if (isAxiosError(err)) {
    return {
      ...base,
      response: err.response,
      config: err.config,
      isAxiosError: true,
    };
  }
  return base;
};

export const classifyAxiosError = (err: unknown): AppError => {
  if (isAppError(err)) return err;

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return withAxiosFields(
      { kind: "offline", message: "navigator.onLine === false", raw: err },
      err
    );
  }

  if (!isAxiosError(err)) {
    return {
      kind: "unknown",
      message: err instanceof Error ? err.message : String(err),
      raw: err,
    };
  }

  if (err.code === "ECONNABORTED" || err.message?.toLowerCase().includes("timeout")) {
    return withAxiosFields({ kind: "timeout", message: err.message, raw: err }, err);
  }

  if (err.code === "ERR_NETWORK" || !err.response) {
    return withAxiosFields(
      { kind: "network", message: err.message || "Network error", raw: err },
      err
    );
  }

  const status = err.response.status;
  const data: any = err.response.data;
  const serverMessage =
    (typeof data === "object" && data && (data.message || data.error?.message)) ||
    (typeof data === "string" ? data : "") ||
    err.message;

  if (status === 401) {
    return withAxiosFields({ kind: "unauthorized", status, message: serverMessage, raw: err }, err);
  }
  if (status === 403) {
    return withAxiosFields({ kind: "forbidden", status, message: serverMessage, raw: err }, err);
  }
  if (status === 404) {
    return withAxiosFields({ kind: "not_found", status, message: serverMessage, raw: err }, err);
  }
  if (status === 429) {
    const retryAfterHeader = err.response.headers?.["retry-after"];
    const retryAfter = retryAfterHeader ? parseInt(String(retryAfterHeader), 10) : undefined;
    return withAxiosFields(
      {
        kind: "rate_limited",
        status,
        message: serverMessage,
        raw: err,
        retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined,
      },
      err
    );
  }
  if (status >= 500) {
    return withAxiosFields({ kind: "server", status, message: serverMessage, raw: err }, err);
  }
  if (status >= 400) {
    return withAxiosFields({ kind: "validation", status, message: serverMessage, raw: err }, err);
  }

  return withAxiosFields({ kind: "unknown", status, message: serverMessage, raw: err }, err);
};
